<template>
    <app-layout>
        <page-header :title="user.name">
            <div class="mt-3 flex items-start justify-between">
                <teleport defer to="#page-header-action">
                    <jet-button v-if="user.links?.edit" type="button" variant="secondary" @click="$inertia.visit(user.links.edit)">
                        {{ trans('general.action.edit') }}
                    </jet-button>
                </teleport>
            </div>
        </page-header>
        <row class="mb-6">
            <div class="w-full rounded-[15px] bg-white p-0">
                <column md="12" xl="6">
                    <card :card-classes="['mb-0']" class="h-full w-full">
                        <div class="d-flex justify-content-between align-items-center">
                            <h4 class="mb-0">{{ user.name }}</h4>
                            <div>
                                <role-badge v-for="role in user.roles" :key="role.id" :role="role" />
                            </div>
                        </div>
                        <email-link :email="user.email" size="sm" />
                        <div class="mt-5 w-full">
                            <h5>{{ trans('users.show.airports.header') }}</h5>
                            <ul v-if="locationDisplayObjects.length > 0" class="list-unstyled mb-0">
                                <li
                                    v-for="locationDisplay in locationDisplayObjects"
                                    :key="locationDisplay.id"
                                    class="mt-2 flex w-full items-start justify-start"
                                >
                                    <div>
                                        <icon
                                            v-if="locationDisplay.countryCodeAlpha2"
                                            v-c-tooltip="{
                                                content: locationDisplay.countryCodeAlpha2,
                                            }"
                                            :name="'cif-' + locationDisplay.countryCodeAlpha2.toLowerCase()"
                                            size="xl"
                                        />
                                    </div>
                                    <div class="mx-2 text-gray-text">{{ locationDisplay.displayName }}</div>
                                    <span
                                        v-if="locationDisplay.countDisplay"
                                        class="ml-auto flex-shrink-0 rounded-full bg-gray-lightest px-2 py-1 text-xs font-medium text-gray-text"
                                        >{{ locationDisplay.countDisplay }}</span
                                    >
                                </li>
                            </ul>
                            <div v-else>
                                {{ trans('users.show.airports.empty') }}
                            </div>
                        </div>
                    </card>
                </column>
            </div>
        </row>
        <row>
            <activity-log
                v-if="hasAnyPermission(['user.activity.read'])"
                :activities="activities"
                :allow-selection="false"
                :fields="fields"
                :query-params="activeQueryParams"
                class="px-0"
                context="user"
            />
        </row>
    </app-layout>
</template>
<script setup>
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';

import AppLayout from '@/Layouts/AppLayout.vue';
import RoleBadge from '@/Components/RoleBadge.vue';
import ActivityLog from '@/Components/ActivityLog/ActivityLog.vue';
import Card from '@/Components/Card.vue';
import EmailLink from '@/Components/EmailLink.vue';
import PageHeader from '@/Components/PageHeader.vue';
import JetButton from '@/Jetstream/Button.vue';

import useQueryParams from '@/Utils/UseQueryParams';
import { buildLocationDisplayObjects } from '@/composables/useHierarchicalLocationDisplay.ts';
import { trans } from '@/mixins/i18n';

// Page and route setup
const page = usePage();
const activeQueryParams = useQueryParams({
    paramsWithDefaults: {
        sort: { column: 'date', asc: false },
    },
    route: route('users.show', page.props.user),
    options: {
        only: ['activities'],
    },
});

// Reactive data
const fields = ref([
    {
        key: 'date',
        label: trans('activities.overview.cell.date'),
        _style: 'width:120px',
    },
    {
        key: 'event',
        label: trans('activities.overview.cell.event'),
        sorter: false,
    },
]);

// Computed properties
const user = computed(() => page.props.user);
const activities = computed(() => page.props.activities);
const hierarchicalLocations = computed(() => page.props.hierarchicalLocations || []);
const userLocationIds = computed(() => user.value.locations?.map((location) => location.id) || []);
const locationDisplayObjects = computed(() => {
    return buildLocationDisplayObjects(hierarchicalLocations.value, userLocationIds.value, {
        includeMetadata: true,
        displayNameBuilder: (top, child) => {
            if (child) {
                return `${top.iataCode} · ${top.name} · ${child.name}`;
            }
            return `${top.iataCode} · ${top.name}`;
        },
    });
});
</script>
